import {
	BeforeInsert,
	BeforeUpdate,
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
} from 'typeorm';
import { normalizePhoneToE164 } from '../../utils/validation';
import { BaseEntity } from './base.entity';
import { VendorEntity } from './vendor.entity';

/**
 * Aggregated mapping of every phone number associated with a vendor.
 * Mirrors `client_phone` for vendors. Populated by `vendorPhoneSync.ts`
 * on every vendor save and kept in canonical E.164 format so the
 * caller-ID matcher can do an exact-string join in sub-50ms.
 *
 * v1 source of phones: `vendor.phone` only. When a vendor-contacts
 * entity arrives, the sync utility extends in one place to include
 * those contact phones too — this table's shape doesn't change.
 */
@Index('vendor_phone_phone_index', ['phone'], {})
@Index('vendor_phone_pk', ['fkVendorId', 'phone'], { unique: true })
@Entity('vendor_phone')
export class VendorPhoneEntity extends BaseEntity {
	@Column('int', { name: 'fk_vendor__id', unsigned: true })
	public fkVendorId!: number;

	@Column('varchar', { name: 'phone', length: 64 })
	public phone!: string;

	@ManyToOne(() => VendorEntity, (vendor) => vendor.vendorPhones, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_vendor__id', referencedColumnName: 'id' }])
	public fkVendor!: VendorEntity;

	// Belt-and-suspenders normalization for any write path that bypasses
	// `syncVendorPhoneNumbers` (the primary normalization point). See
	// the matching hook on `ClientPhoneEntity` for the reasoning.
	@BeforeInsert()
	@BeforeUpdate()
	// `protected` (not `private`): this hook is invoked by TypeORM via the
	// @BeforeInsert/@BeforeUpdate decorators, not by direct calls, so TS
	// `noUnusedLocals` (TS6133) would wrongly flag a `private` version as unused.
	protected _normalizePhone(): void {
		if (this.phone != null && this.phone !== '') {
			const normalized = normalizePhoneToE164(this.phone);
			if (normalized != null) {
				this.phone = normalized;
			}
		}
	}
}
